import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Icon, Drawer } from '../components/ui';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';
import './LiveMcpFlow.css';

/**
 * Live MCP Flow — every tool call through the Agenti5 MCP gateway, animated
 * as a dot travelling User → Claude → Gateway → MCP server and back, coloured
 * by what the gateway did with it. Reads audit_logs only (polled every 2s).
 */

interface LiveMcpFlowProps {
  apiUrl: string;
}

interface Upstream { slug: string; name: string; client_agent_name: string }

interface Row {
  id: number;
  timestamp: string;
  service: string;
  tool_name: string;
  status: string;
  end_user?: string;
  arguments?: string;
  result?: string;
  detail?: string;
}

interface Span { category: string; start: number; end: number; match: string; location: string; context: string }
interface Detail {
  identity?: { user: string | null; groups: string[]; verified: boolean; source: string };
  verdict?: { action: string; detectors?: string[]; score?: number; spans?: Span[]; reason?: string };
  input_dlp?: { types: string[] };
  output_dlp?: { mode: string; types: string[] };
  latency_ms?: number;
}

type Verdict = 'blocked' | 'neutralized' | 'flagged' | 'allowed' | 'error';

interface Flight { key: string; row: Row; verdict: Verdict; lane: number; reached: boolean; t0: number }

const POLL_MS = 2000;
const FLIGHT_MS = 2600;
const STAGGER_MS = 450;
const TICKER_MAX = 100;

const verdictOf = (status: string): Verdict => {
  const s = (status || '').toLowerCase();
  if (['policy_denied', 'mcp_injection_blocked', 'mcp_tool_poisoned', 'blocked', 'dlp_blocked'].includes(s)) return 'blocked';
  if (s === 'mcp_injection_neutralized') return 'neutralized';
  if (['mcp_injection_detected', 'dlp_flagged'].includes(s)) return 'flagged';
  if (['error', 'upstream_error'].includes(s)) return 'error';
  return 'allowed';
};
// Denials and tool-pin holds are decided before the call leaves the gateway.
const reachesUpstream = (status: string) => !['policy_denied', 'mcp_tool_poisoned'].includes((status || '').toLowerCase());

const parse = <T,>(raw?: string): T | null => {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};
const pretty = (raw?: string) => {
  const v = parse<unknown>(raw);
  return v === null ? raw || '' : JSON.stringify(v, null, 2);
};

// ---- geometry (SVG user units) ----
const W = 1000;
const X = { user: 90, claude: 330, gateway: 590, server: 880 };
type Pt = [number, number];

function lerpPath(pts: Pt[], p: number): Pt {
  const lens = pts.slice(1).map((b, i) => Math.hypot(b[0] - pts[i][0], b[1] - pts[i][1]));
  const total = lens.reduce((a, b) => a + b, 0);
  let d = Math.min(Math.max(p, 0), 1) * total;
  for (let i = 0; i < lens.length; i++) {
    if (d <= lens[i] || i === lens.length - 1) {
      const f = lens[i] ? d / lens[i] : 0;
      return [pts[i][0] + (pts[i + 1][0] - pts[i][0]) * f, pts[i][1] + (pts[i + 1][1] - pts[i][1]) * f];
    }
    d -= lens[i];
  }
  return pts[pts.length - 1];
}
/** Fraction of the path at which the dot is back at the gateway (verdict applied). */
function gatewayReturnAt(pts: Pt[]): number {
  const lens = pts.slice(1).map((b, i) => Math.hypot(b[0] - pts[i][0], b[1] - pts[i][1]));
  const total = lens.reduce((a, b) => a + b, 0);
  const lastG = pts.map((q) => q[0]).lastIndexOf(X.gateway);
  return lens.slice(0, lastG).reduce((a, b) => a + b, 0) / total;
}

/** The output text with each detector span marked. */
const MarkedText: React.FC<{ text: string; spans: Span[] }> = ({ text, spans }) => {
  // Merge overlapping spans ("assistant: forget" + "forget all instructions") into one mark.
  const merged: Array<{ start: number; end: number; cats: string[] }> = [];
  [...spans].filter((s) => s.end > s.start && s.end <= text.length).sort((a, b) => a.start - b.start).forEach((s) => {
    const last = merged[merged.length - 1];
    if (last && s.start <= last.end) {
      last.end = Math.max(last.end, s.end);
      if (!last.cats.includes(s.category)) last.cats.push(s.category);
    } else merged.push({ start: s.start, end: s.end, cats: [s.category] });
  });
  const parts: React.ReactNode[] = [];
  let at = 0;
  for (const m of merged) {
    if (m.start > at) parts.push(text.slice(at, m.start));
    parts.push(<mark key={m.start} className="lf-mark" title={m.cats.join(', ')}>{text.slice(m.start, m.end)}</mark>);
    at = m.end;
  }
  parts.push(text.slice(at));
  return <>{parts}</>;
};

const MAX_LANES = 8; // lanes shown automatically, busiest first
const MAX_PICKED = 40;

const LiveMcpFlow: React.FC<LiveMcpFlowProps> = ({ apiUrl }) => {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState<Upstream[]>([]);
  const [picked, setPicked] = useState<Set<string> | null>(null); // null = auto: the busiest few
  const [zoom, setZoom] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterQ, setFilterQ] = useState('');
  const [ticker, setTicker] = useState<Row[]>([]);
  const [flights, setFlights] = useState<Flight[]>([]);
  const [now, setNow] = useState(Date.now());
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Row | null>(null);
  const [counts, setCounts] = useState<Record<Verdict, number>>({ blocked: 0, neutralized: 0, flagged: 0, allowed: 0, error: 0 });
  const lastId = useRef<number | null>(null);
  const nextSlot = useRef(0);

  const loadUpstreams = useCallback(async () => {
    try {
      // Only servers that have carried traffic (last 30 days), busiest first and capped so the
      // tree stays readable; registered-but-idle servers stay on the Cloud MCP Gateway screen.
      const r = await fetch(`${apiUrl}/admin/mcp-gateway/overview?sort=calls&dir=desc&page_size=100&window=720`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || r.statusText);
      const active = (d.upstreams || []).filter((u: any) => u.calls > 0); // already busiest first
      setBusy(active.map((u: any) => ({ slug: u.slug, name: u.name, client_agent_name: u.client_agent_name })));
    } catch (e: any) {
      setError(e.message || 'Could not reach the firewall API.');
    }
  }, [apiUrl]);

  useEffect(() => {
    loadUpstreams();
    const h = setInterval(loadUpstreams, 30000);
    return () => clearInterval(h);
  }, [loadUpstreams]);

  const upstreams = useMemo(() => (
    (picked ? busy.filter((u) => picked.has(u.slug)).slice(0, MAX_PICKED) : busy.slice(0, MAX_LANES))
      .slice().sort((a, b) => a.name.localeCompare(b.name))
  ), [busy, picked]);
  const slugs = upstreams.map((u) => u.slug).join(',');

  const poll = useCallback(async () => {
    if (!slugs) return;
    const first = lastId.current === null;
    const q = `service IN (${slugs})${first ? '' : ` id>${lastId.current}`}`;
    const params = new URLSearchParams({ q, limit: first ? '30' : '50', offset: '0', sort: first ? 'DESC' : 'asc' });
    try {
      const r = await fetch(`${apiUrl}/admin/audit-logs/search?${params}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || r.statusText);
      setError('');
      const rows: Row[] = d.logs || [];
      if (first) {
        lastId.current = rows.length ? Math.max(...rows.map((x) => x.id)) : 0;
        setTicker(rows);
        return;
      }
      if (!rows.length) return;
      lastId.current = Math.max(lastId.current || 0, ...rows.map((x) => x.id));
      setTicker((prev) => [...[...rows].reverse(), ...prev].slice(0, TICKER_MAX));
      const lanes = slugs.split(',');
      const start = Math.max(Date.now(), nextSlot.current);
      const fresh = rows.map((row, i) => ({
        key: `f${row.id}`, row, verdict: verdictOf(row.status), lane: Math.max(0, lanes.indexOf(row.service)),
        reached: reachesUpstream(row.status), t0: start + i * STAGGER_MS,
      }));
      nextSlot.current = start + rows.length * STAGGER_MS;
      setFlights((prev) => [...prev, ...fresh].slice(-40));
      setCounts((c) => {
        const n = { ...c };
        fresh.forEach((f) => { n[f.verdict] += 1; });
        return n;
      });
    } catch (e: any) {
      setError(e.message || 'Could not reach the firewall API.');
    }
  }, [apiUrl, slugs]);

  useEffect(() => {
    if (paused) return;
    poll();
    const h = setInterval(poll, POLL_MS);
    return () => clearInterval(h);
  }, [poll, paused]);

  // Animation clock: runs only while something is in the air.
  useEffect(() => {
    if (!flights.length) return;
    let raf = 0;
    const tick = () => {
      const tNow = Date.now();
      setNow(tNow);
      setFlights((prev) => {
        const live = prev.filter((f) => tNow - f.t0 < FLIGHT_MS + 400);
        return live.length === prev.length ? prev : live;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [flights.length]);

  const n = Math.max(upstreams.length, 1);
  const lanePitch = n > 10 ? 64 : 96;
  const H = Math.max(280, n * lanePitch + 80);
  const cy = H / 2 + 14;
  const laneY = (i: number) => (n === 1 ? cy : 70 + ((H - 110) * i) / (n - 1));
  const pathFor = (f: Flight): Pt[] => {
    const u: Pt = [X.user, cy], c: Pt = [X.claude, cy], g: Pt = [X.gateway, cy], s: Pt = [X.server, laneY(f.lane)];
    return f.reached ? [u, c, g, s, g, c, u] : [u, c, g, c, u];
  };

  const lastUser = ticker[0]?.end_user || '—';
  const client = upstreams[0]?.client_agent_name || 'Claude';
  const fmtTime = (ts: string) => parseServerDate(ts).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const node = (x: number, y: number, title: string, sub: string, cls: string, w = 170) => (
    <g className={`lf-node ${cls}`} transform={`translate(${x - w / 2}, ${y - 28})`}>
      <rect width={w} height={56} rx={8} />
      <text x={w / 2} y={24} textAnchor="middle" className="lf-node-title">{title}</text>
      <text x={w / 2} y={42} textAnchor="middle" className="lf-node-sub">{sub.length > 26 ? sub.slice(0, 25) + '…' : sub}</text>
    </g>
  );

  const detail = selected ? parse<Detail>(selected.detail) : null;
  const spans = detail?.verdict?.spans || [];
  const output = (() => {
    if (!selected) return { text: '', marked: false };
    const res = parse<any>(selected.result);
    const first = res?.content?.[0]?.text;
    if (typeof first === 'string') {
      return { text: first, marked: spans.some((s) => s.location === 'content[0].text') };
    }
    return { text: pretty(selected.result), marked: false };
  })();
  const outputSpans = spans.filter((s) => s.location === 'content[0].text');
  // Same matches repeat in structuredContent; list them only when nothing was marked in the text.
  const otherSpans = output.marked ? [] : spans.filter((s) => s.location !== 'content[0].text');

  return (
    <div className="page-content lf-root">
      <div className="page-title">
        <div>
          <h1><Icon name="bolt" size={20} /> {t('pages.liveFlow.title')}</h1>
          <p className="subtitle">{t('pages.liveFlow.subtitle')}</p>
        </div>
        <div className="ent-toolbar" style={{ margin: 0 }}>
          <span className={`lf-live ${paused ? 'paused' : ''}`}><span className="lf-live-dot" />{paused ? t('pages.liveFlow.paused') : t('pages.liveFlow.live')}</span>
          <div className="lf-zoom" role="group" aria-label={t('pages.liveFlow.zoom', { defaultValue: 'Zoom' })}>
            <button className="ent-btn sm" onClick={() => setZoom((z) => Math.max(0.3, +(z - 0.2).toFixed(2)))} aria-label={t('pages.liveFlow.zoomOut', { defaultValue: 'Zoom out' })}>−</button>
            <button className="ent-btn sm" onClick={() => setZoom(1)} title={t('pages.liveFlow.zoomReset', { defaultValue: 'Reset zoom' })}>{Math.round(zoom * 100)}%</button>
            <button className="ent-btn sm" onClick={() => setZoom((z) => Math.min(2, +(z + 0.2).toFixed(2)))} aria-label={t('pages.liveFlow.zoomIn', { defaultValue: 'Zoom in' })}>+</button>
          </div>
          <div className="lf-filter">
            <button className="ent-btn" onClick={() => setFilterOpen((o) => !o)} aria-expanded={filterOpen}>
              <Icon name="search" /> {picked ? t('pages.liveFlow.filterCount', { defaultValue: '{{count}} of {{total}} servers', count: upstreams.length, total: busy.length }) : t('pages.liveFlow.filterAuto', { defaultValue: 'Busiest {{count}} servers', count: Math.min(MAX_LANES, busy.length) })}
            </button>
            {filterOpen && (
              <div className="lf-filter-pop">
                <input className="mg-search" autoFocus value={filterQ} onChange={(e) => setFilterQ(e.target.value)} placeholder={t('pages.liveFlow.filterSearch', { defaultValue: 'Search servers with traffic…' })} />
                <div className="lf-filter-actions">
                  <button className="ent-btn sm ghost" onClick={() => setPicked(null)}>{t('pages.liveFlow.filterAutoBtn', { defaultValue: 'Auto (busiest)' })}</button>
                  <button className="ent-btn sm ghost" onClick={() => setPicked(new Set(busy.slice(0, MAX_PICKED).map((u) => u.slug)))}>{t('pages.liveFlow.filterAll', { defaultValue: 'Show all' })}</button>
                  <button className="ent-btn sm ghost" onClick={() => setPicked(new Set())}>{t('pages.liveFlow.filterNone', { defaultValue: 'None' })}</button>
                </div>
                <div className="lf-filter-list">
                  {busy.filter((u) => !filterQ || `${u.name} ${u.slug}`.toLowerCase().includes(filterQ.toLowerCase())).map((u) => {
                    const on = picked ? picked.has(u.slug) : upstreams.some((x) => x.slug === u.slug);
                    return (
                      <label key={u.slug} className="lf-filter-row">
                        <input type="checkbox" checked={on} onChange={() => {
                          const base = new Set(picked || upstreams.map((x) => x.slug));
                          if (on) base.delete(u.slug); else base.add(u.slug);
                          setPicked(base);
                        }} />
                        <span>{u.name}</span>
                      </label>
                    );
                  })}
                  {!busy.length && <div className="lf-filter-empty">{t('pages.liveFlow.noUpstreams')}</div>}
                </div>
              </div>
            )}
          </div>
          <button className="ent-btn" onClick={() => setPaused((p) => !p)}>
            <Icon name={paused ? 'power' : 'clock'} /> {paused ? t('pages.liveFlow.resume') : t('pages.liveFlow.pause')}
          </button>
        </div>
      </div>

      {error && <div className="notice error"><Icon name="warning" /> {error}</div>}

      <div className="lf-counts">
        {(['allowed', 'neutralized', 'blocked'] as Verdict[]).map((v) => (
          <div key={v} className={`lf-count lf-v-${v}`}>
            <span className="lf-count-num">{counts[v]}</span>
            <span className="lf-count-label">{t(`pages.liveFlow.v_${v}`)}</span>
          </div>
        ))}
        <div className="lf-legend">
          {(['allowed', 'neutralized', 'blocked'] as Verdict[]).map((v) => (
            <span key={v}><i className={`lf-swatch lf-v-${v}`} />{t(`pages.liveFlow.legend_${v}`)}</span>
          ))}
        </div>
      </div>

      <div className="lf-stage">
        <svg viewBox={`0 0 ${W} ${H}`} className="lf-svg" style={{ width: `${zoom * 100}%`, minWidth: zoom < 1 ? 0 : undefined }} role="img" aria-label={t('pages.liveFlow.title')}>
          {[['user', X.user], ['claude', X.claude], ['gateway', X.gateway], ['servers', X.server]].map(([k, x]) => (
            <text key={k as string} x={x as number} y={22} textAnchor="middle" className="lf-lane-label">{t(`pages.liveFlow.lane_${k}`)}</text>
          ))}
          <line x1={X.user} y1={cy} x2={X.gateway} y2={cy} className="lf-wire" />
          {upstreams.map((u, i) => (
            <path key={u.slug} d={`M ${X.gateway} ${cy} C ${X.gateway + 140} ${cy}, ${X.server - 150} ${laneY(i)}, ${X.server} ${laneY(i)}`} className="lf-wire" />
          ))}
          {node(X.user, cy, t('pages.liveFlow.lane_user'), lastUser, 'user')}
          {node(X.claude, cy, client, t('pages.liveFlow.claudeSub'), 'claude')}
          {node(X.gateway, cy, 'Agenti5', t('pages.liveFlow.gatewaySub'), 'gateway')}
          {upstreams.map((u, i) => <React.Fragment key={u.slug}>{node(X.server, laneY(i), u.name, `/mcp-gw/${u.slug}`, 'server', 200)}</React.Fragment>)}
          {!upstreams.length && node(X.server, cy, t('pages.liveFlow.noUpstreams'), '', 'server', 200)}

          {flights.map((f) => {
            const p = (now - f.t0) / FLIGHT_MS;
            if (p < 0 || p > 1.15) return null;
            const pts = pathFor(f);
            const pg = gatewayReturnAt(pts);
            const [x, y] = lerpPath(pts, p);
            const judged = p >= pg;
            const ringP = (p - pg) / 0.18;
            return (
              <g key={f.key}>
                {ringP >= 0 && ringP <= 1 && (
                  <circle cx={X.gateway} cy={cy} r={18 + ringP * 46} className={`lf-ring lf-v-${f.verdict}`} style={{ opacity: 1 - ringP }} />
                )}
                {p <= 1 && <circle cx={x} cy={y} r={judged ? 9 : 7} className={`lf-dot ${judged ? `lf-v-${f.verdict}` : 'lf-dot-pending'}`} />}
              </g>
            );
          })}
        </svg>
      </div>

      <div className="ent-card lf-ticker">
        <div className="lf-ticker-head">
          <h3>{t('pages.liveFlow.ticker')}</h3>
          <span className="lf-hint">{t('pages.liveFlow.tickerHint')}</span>
        </div>
        <div className="ent-table-wrap scroll">
          <table className="ent-table">
            <thead>
              <tr>
                <th>{t('pages.liveFlow.time')}</th>
                <th>{t('pages.liveFlow.user')}</th>
                <th>{t('pages.liveFlow.server')}</th>
                <th>{t('pages.liveFlow.tool')}</th>
                <th>{t('pages.liveFlow.verdict')}</th>
              </tr>
            </thead>
            <tbody>
              {!ticker.length && (
                <tr><td colSpan={5} className="lf-empty">{t('pages.liveFlow.noEvents')}</td></tr>
              )}
              {ticker.map((r) => {
                const v = verdictOf(r.status);
                return (
                  <tr key={r.id} className="lf-row" onClick={() => setSelected(r)}>
                    <td className="mono">{fmtTime(r.timestamp)}</td>
                    <td>{r.end_user || '—'}</td>
                    <td>{upstreams.find((u) => u.slug === r.service)?.name || r.service}</td>
                    <td className="mono">{r.tool_name}</td>
                    <td><span className={`lf-chip lf-v-${v}`}>{t(`pages.liveFlow.v_${v}`)}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <Drawer title={`${selected.tool_name} · ${upstreams.find((u) => u.slug === selected.service)?.name || selected.service}`} onClose={() => setSelected(null)} width={620}>
          <div className="lf-evidence">
            <div className="lf-ev-head">
              <span className={`lf-chip lg lf-v-${verdictOf(selected.status)}`}>{t(`pages.liveFlow.v_${verdictOf(selected.status)}`)}</span>
              <span className="mono lf-hint">{parseServerDate(selected.timestamp).toLocaleString(i18n.language)} · #{selected.id}</span>
            </div>

            <div className="lf-ev-grid">
              <div><label>{t('pages.liveFlow.user')}</label>{selected.end_user || '—'}</div>
              <div><label>{t('pages.liveFlow.groups')}</label>{detail?.identity?.groups?.join(', ') || '—'}</div>
              <div><label>{t('pages.liveFlow.action')}</label>{detail?.verdict?.action || (verdictOf(selected.status) === 'allowed' ? t('pages.liveFlow.forwarded') : selected.status)}</div>
              <div><label>{t('pages.liveFlow.latency')}</label>{detail?.latency_ms != null ? `${detail.latency_ms} ms` : '—'}</div>
            </div>
            {detail?.verdict?.reason && <div className="lf-reason"><Icon name="info" /> {detail.verdict.reason}</div>}

            <section>
              <h4>{t('pages.liveFlow.detectors')}</h4>
              {detail?.verdict?.detectors?.length ? (
                <div className="lf-detectors">{detail.verdict.detectors.map((d) => <span key={d} className="lf-chip lf-v-blocked">{d}</span>)}</div>
              ) : <span className="lf-hint">{t('pages.liveFlow.noDetectors')}</span>}
              {(detail?.input_dlp?.types?.length || detail?.output_dlp?.types?.length) ? (
                <div className="lf-hint" style={{ marginTop: 6 }}>DLP: {[...(detail?.input_dlp?.types || []), ...(detail?.output_dlp?.types || [])].join(', ')}</div>
              ) : null}
            </section>

            <section>
              <h4>{t('pages.liveFlow.inputs')}</h4>
              <pre className="lf-pre">{pretty(selected.arguments) || '{}'}</pre>
            </section>

            <section>
              <h4>{t('pages.liveFlow.output')}{outputSpans.length > 0 && <span className="lf-hint"> — {t('pages.liveFlow.injectedNote')}</span>}</h4>
              <pre className="lf-pre lf-out">
                {output.marked ? <MarkedText text={output.text} spans={outputSpans} /> : output.text || '—'}
              </pre>
              {otherSpans.length > 0 && (
                <div className="lf-other-spans">
                  {otherSpans.map((s, i) => (
                    <div key={i}><span className="lf-chip lf-v-blocked">{s.category}</span> <code>{s.location}</code> <mark className="lf-mark">{s.match}</mark></div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </Drawer>
      )}
    </div>
  );
};

export default LiveMcpFlow;
